// src/components/chat/ChatDock.jsx — Persistent Chat Dock Container
import React, { useState } from "react";
import { MessageSquare, Clock, Settings, X, Sparkles } from "lucide-react";
import { useChat } from "../../context/ChatContext";
import TeamChatPanel from "./TeamChatPanel";
import BuddyPanel from "./BuddyPanel";
import RemindersPanel from "./RemindersPanel";
import ChatSettingsModal from "./ChatSettingsModal";
import "./chat.css";

export default function ChatDock() {
  const {
    isDockOpen,
    setIsDockOpen,
    activeTab,
    setActiveTab,
    unreadTotal,
    isReconnecting
  } = useChat();

  const [showSettings, setShowSettings] = useState(false);

  return (
    <>
      {/* ── 1. Collapsed Trigger Button ── */}
      {!isDockOpen && (
        <button
          className="chat-dock-trigger"
          onClick={() => setIsDockOpen(true)}
          title="Open Team Chat & Buddy"
          data-tour="chat-dock-launcher"
        >
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <MessageSquare size={18} className="text-primary" />
            {isReconnecting && (
              <span
                style={{
                  position: "absolute",
                  top: -2,
                  right: -2,
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#ff9f0a"
                }}
              />
            )}
          </div>
          <span>Chat</span>
          {unreadTotal > 0 && (
            <span className="unread-pill">{unreadTotal > 99 ? "99+" : unreadTotal}</span>
          )}
        </button>
      )}

      {/* ── 2. Expanded Side Panel / Bottom Sheet (Persistent in DOM so conversation never disappears on close) ── */}
      <div 
        className="chat-dock-panel"
        style={{ display: isDockOpen ? "flex" : "none" }}
      >
        {/* Header */}
        <div className="chat-panel-header">
          {/* Tab Group */}
          <div className="chat-tab-group">
            <button
              className={`chat-tab-btn ${activeTab === "chat" ? "active" : ""}`}
              onClick={() => setActiveTab("chat")}
            >
              <MessageSquare size={14} /> Team
              {unreadTotal > 0 && activeTab !== "chat" && (
                <span
                  style={{
                    background: "var(--danger, #ff453a)",
                    color: "#fff",
                    fontSize: "9px",
                    padding: "1px 5px",
                    borderRadius: "9999px"
                  }}
                >
                  {unreadTotal}
                </span>
              )}
            </button>

            <button
              className={`chat-tab-btn ${activeTab === "buddy" ? "active" : ""}`}
              onClick={() => setActiveTab("buddy")}
            >
              <Sparkles size={14} /> Buddy
            </button>

            <button
              className={`chat-tab-btn ${activeTab === "reminders" ? "active" : ""}`}
              onClick={() => setActiveTab("reminders")}
            >
              <Clock size={14} /> Reminders
            </button>
          </div>

          {/* Header Actions */}
          <div className="chat-header-actions">
            <button
              className="chat-icon-btn"
              onClick={() => setShowSettings(true)}
              title="Chat & Assistant Settings"
            >
              <Settings size={15} />
            </button>

            <button
              className="chat-icon-btn"
              onClick={() => setIsDockOpen(false)}
              title="Minimize Chat"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body — subpanels keep state across tab switches */}
        <div className="chat-panel-body">
          <div style={{ display: activeTab === "chat" ? "flex" : "none", height: "100%", width: "100%" }}>
            <TeamChatPanel />
          </div>
          <div style={{ display: activeTab === "buddy" ? "flex" : "none", height: "100%", width: "100%" }}>
            <BuddyPanel />
          </div>
          <div style={{ display: activeTab === "reminders" ? "flex" : "none", height: "100%", width: "100%" }}>
            <RemindersPanel />
          </div>
        </div>
      </div>

      {/* ── 3. Settings Modal ── */}
      <ChatSettingsModal show={showSettings} onClose={() => setShowSettings(false)} />
    </>
  );
}
